import { useMemo } from 'react'

import { calculateRequiredCoverage, ZoneParams } from './zoneAnalysisApi'

export interface OverviewRow {
  key: string
  zone: number
  description: string
  damageMechanisms: string
  coveragePercent: string
  coverageArea: string
}

export type ZoneComputationParams = {
  zones: ZoneParams[]
  cofModifier: number
  confidence: 'low' | 'med' | 'high'
}

/**
 * Pure builder for a single zone overview row — same formula and formatting as
 * `useComputedZoneData` so both Libra (via the hook) and non-React callers
 * (e.g. the drilldown by-zone table) render identical coverage strings.
 */
export function buildZoneOverviewRow(
  zone: ZoneParams,
  index: number,
  cofModifier: number,
  confidence: 'low' | 'med' | 'high'
): OverviewRow {
  const coverage = calculateRequiredCoverage(zone, cofModifier, confidence)
  const dms = zone.damage_mechanisms?.map((dm) => dm.name).join(', ') || '—'
  return {
    key: zone.id,
    zone: index + 1,
    description: zone.description || zone.title,
    damageMechanisms: dms,
    coveragePercent:
      coverage?.required_coverage_percent !== undefined
        ? `${coverage.required_coverage_percent.toFixed(2)}%`
        : '—',
    coverageArea:
      coverage?.required_coverage_area_sq_m !== undefined
        ? `${coverage.required_coverage_area_sq_m.toFixed(2)} m²`
        : '—',
  }
}

export const useComputedZoneData = ({
  zones,
  cofModifier,
  confidence,
}: ZoneComputationParams) =>
  useMemo<OverviewRow[]>(
    () =>
      zones.map((zone, idx) =>
        buildZoneOverviewRow(zone, idx, cofModifier, confidence)
      ),
    [zones, cofModifier, confidence]
  )
